import { useState, useEffect, useRef } from 'react';
import { Upload, User, Mail, Phone, Briefcase, FileText, Book, Calendar, Clock, MapPin } from 'lucide-react';
import emailjs from '@emailjs/browser';

export default function GeneralApplicationForm() {
  const formRef = useRef();
  
  // Initialize state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    resumeFile: null,
    portfolioLink: '',
    coverLetterFile: null,
    availability: '',
    startDate: '',
    workAuthorization: '',
    desiredRole: '',
    yearsExperience: '',
    heardFrom: '',
    comments: '',
    notificationEmail: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resumeFileName, setResumeFileName] = useState('');
  const [coverLetterFileName, setCoverLetterFileName] = useState('');

  // Initialize EmailJS once on component mount
  useEffect(() => {
    // Replace with your actual EmailJS user ID
    emailjs.init("giRLVQjBjMa6ybFTh");
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files.length > 0) {
      if (name === 'resumeFile') {
        setResumeFileName(files[0].name);
      } else if (name === 'coverLetterFile') {
        setCoverLetterFileName(files[0].name);
      }
      setFormData({ ...formData, [name]: files[0] });
    }
  };
  
  const sendEmail = async () => {
    // Prepare template parameters
    const templateParams = {
      to_email: '4.finger.studios@gmail.com', // Send directly to the studio
      from_name: 'Job Application System',
      reply_to: formData.email,
      applicant_name: `${formData.firstName} ${formData.lastName}`,
      applicant_email: formData.email,
      notification_email: formData.notificationEmail,
      applicant_phone: formData.phone,
      applicant_location: formData.location,
      desired_role: formData.desiredRole,
      years_experience: formData.yearsExperience,
      availability: formData.availability,
      start_date: formData.startDate,
      work_authorization: formData.workAuthorization,
      portfolio_link: formData.portfolioLink || 'Not provided',
      heard_from: formData.heardFrom || 'Not specified',
      resume_file_name: resumeFileName || 'Not provided',
      cover_letter_file_name: coverLetterFileName || 'Not provided',
      comments: formData.comments || 'None'
    };
  
    try {
      // Send email using EmailJS
      const response = await emailjs.send(
        'service_1v2v4no',  // Replace with your service ID
        'template_2f4678r',  // Replace with your template ID
        templateParams
      );
      
      console.log('Email successfully sent:', response);
      return response;
    } catch (error) {
      console.error('Error sending email:', error);
      throw new Error(`Failed to send email: ${error.text || 'Unknown error'}`);
    }
  };

  const validateForm = () => {
    // Check required fields
    const requiredFields = [
      'firstName', 'lastName', 'email', 'phone', 'location',
      'desiredRole', 'yearsExperience', 'availability', 'startDate',
      'workAuthorization', 'notificationEmail'
    ];
    
    for (const field of requiredFields) {
      if (!formData[field]) {
        return `Please fill in all required fields`;
      }
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return 'Please enter a valid email address';
    }
    
    if (!emailRegex.test(formData.notificationEmail)) {
      return 'Please enter a valid notification email address';
    }
    
    // Check if resume is uploaded
    if (!resumeFileName) {
      return 'Please upload your resume';
    }
    
    return null; // No errors
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('Form submitted:', formData);
      
      // Send email notification
      await sendEmail();
      
      // If everything is successful
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(`There was a problem submitting your application: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full text-center">
          <h2 className="text-3xl font-bold text-green-600 mb-4">Application Submitted!</h2>
          <p className="text-gray-700 mb-6">
            Thank you for your interest in joining 4 Finger Studios. We've received your application and will review it shortly.
            Our team will contact you if your qualifications match our current openings.
          </p>
          <p className="text-gray-700 mb-6">
            A confirmation email has been sent to your provided email address.
          </p>
          <button 
            onClick={() => {
              setSubmitted(false);
              setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                location: '',
                resumeFile: null,
                portfolioLink: '',
                coverLetterFile: null,
                availability: '',
                startDate: '',
                workAuthorization: '',
                desiredRole: '',
                yearsExperience: '',
                heardFrom: '',
                comments: '',
                notificationEmail: ''
              });
              setResumeFileName('');
              setCoverLetterFileName('');
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-md font-medium transition-colors"
          >
            Submit Another Application
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-blue-600 px-6 py-8">
            <div className="text-center">
              <h1 className="text-3xl font-bold text-white">General Application</h1>
              <p className="mt-2 text-blue-100">Join the talented team at 4 Finger Studios</p>
            </div>
          </div>
          
          {/* Form Content */}
          <form ref={formRef} onSubmit={handleSubmit} className="px-6 py-8 space-y-8">
            {/* Personal Information */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <User className="mr-2 text-gray-500" size={20} /> First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="John"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <User className="mr-2 text-gray-500" size={20} /> Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Doe"
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Mail className="mr-2 text-gray-500" size={20} /> Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="john.doe@example.com"
                />
              </div>
              <div>
                <label htmlFor="notificationEmail" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Mail className="mr-2 text-gray-500" size={20} /> Notification Email
                </label>
                <input
                  type="email"
                  id="notificationEmail"
                  name="notificationEmail"
                  value={formData.notificationEmail}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Alternate email for notifications"
                />
              </div>
            </div>

            {/* Phone and Location */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Phone className="mr-2 text-gray-500" size={20} /> Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="(123) 456-7890"
                />
              </div>
              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <MapPin className="mr-2 text-gray-500" size={20} /> Location
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="City, State/Province"
                />
              </div>
            </div>

            {/* Professional Details */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="desiredRole" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Briefcase className="mr-2 text-gray-500" size={20} /> Desired Role
                </label>
                <input
                  type="text"
                  id="desiredRole"
                  name="desiredRole"
                  value={formData.desiredRole}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Game Developer, Artist, etc."
                />
              </div>
              <div>
                <label htmlFor="yearsExperience" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Book className="mr-2 text-gray-500" size={20} /> Years of Experience
                </label>
                <select
                  id="yearsExperience"
                  name="yearsExperience"
                  value={formData.yearsExperience}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Years of Experience</option>
                  <option value="0-1">0-1 years</option>
                  <option value="1-3">1-3 years</option>
                  <option value="3-5">3-5 years</option>
                  <option value="5-10">5-10 years</option>
                  <option value="10+">10+ years</option>
                </select>
              </div>
            </div>

            {/* Availability and Start Date */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="availability" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Calendar className="mr-2 text-gray-500" size={20} /> Availability
                </label>
                <select
                  id="availability"
                  name="availability"
                  value={formData.availability}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Availability</option>
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Freelance">Freelance</option>
                </select>
              </div>
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Clock className="mr-2 text-gray-500" size={20} /> Earliest Start Date
                </label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Work Authorization */}
            <div>
              <label htmlFor="workAuthorization" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FileText className="mr-2 text-gray-500" size={20} /> Work Authorization
              </label>
              <select
                id="workAuthorization"
                name="workAuthorization"
                value={formData.workAuthorization}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Work Authorization</option>
                <option value="US Citizen">US Citizen</option>
                <option value="Permanent Resident">Permanent Resident</option>
                <option value="Work Visa">Work Visa</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* File Uploads */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="resumeFile" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Upload className="mr-2 text-gray-500" size={20} /> Resume
                </label>
                <div className="flex items-center">
                  <input
                    type="file"
                    id="resumeFile"
                    name="resumeFile"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label 
                    htmlFor="resumeFile" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 flex items-center"
                  >
                    {resumeFileName ? resumeFileName : 'Choose Resume'}
                  </label>
                </div>
              </div>
              <div>
                <label htmlFor="coverLetterFile" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Upload className="mr-2 text-gray-500" size={20} /> Cover Letter (Optional)
                </label>
                <div className="flex items-center">
                  <input
                    type="file"
                    id="coverLetterFile"
                    name="coverLetterFile"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label 
                    htmlFor="coverLetterFile" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 flex items-center"
                  >
                    {coverLetterFileName ? coverLetterFileName : 'Choose Cover Letter'}
                  </label>
                </div>
              </div>
            </div>

            {/* Portfolio and Referral */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="portfolioLink" className="block text-sm font-medium text-gray-700 mb-2">
                  Portfolio Link (Optional)
                </label>
                <input
                  type="url"
                  id="portfolioLink"
                  name="portfolioLink"
                  value={formData.portfolioLink}
                  onChange={handleChange}
                  placeholder="https://yourportfolio.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="heardFrom" className="block text-sm font-medium text-gray-700 mb-2">
                  How did you hear about us?
                </label>
                <select
                  id="heardFrom"
                  name="heardFrom"
                  value={formData.heardFrom}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select an option</option>
                  <option value="LinkedIn">LinkedIn</option>
                  <option value="Indeed">Indeed</option>
                  <option value="Referral">Referral</option>
                  <option value="Company Website">Company Website</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {/* Additional Comments */}
            <div>
              <label htmlFor="comments" className="block text-sm font-medium text-gray-700 mb-2">
                Additional Comments
              </label>
              <textarea
                id="comments"
                name="comments"
                value={formData.comments}
                onChange={handleChange}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Any additional information you'd like to share"
              ></textarea>
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-300 text-red-800 rounded-md p-4">
                <p>{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={loading}
                className={`w-full ${loading ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'} text-white py-3 px-6 rounded-md font-medium transition-colors flex items-center justify-center`}
              >
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
              <p className="mt-4 text-sm text-center text-gray-500">
                By submitting this application, you confirm that all information provided is accurate and complete.
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}